import { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, Platform, Linking } from 'react-native';
import { Box, Typography, Button, useTheme, useMediaQuery, Fab } from '@mui/material';
import { useIsFocused } from '@react-navigation/native';
import { navigationStore, ScreenHeroConfig } from '../../../../app/stores/Navigation';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';
import { useScreenComponent } from '../../../../app/hooks/custom/useScreenComponent';
import { ScreenComponent } from '../../../../models/enums/ScreenComponents';
import { AppLogo } from '../../../../components/AppLogo';
import { VideoBackground } from '../../../../components/VideoBackground';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { RestaurantSignupForm } from './components/RestaurantSignupForm';

// CSS Keyframes for animations
const keyframes = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(40px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes fadeInLeft {
        from {
            opacity: 0;
            transform: translateX(-40px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    
    @keyframes fadeInRight {
        from {
            opacity: 0;
            transform: translateX(40px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    
    @keyframes scaleIn {
        from {
            opacity: 0;
            transform: scale(0.8);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
    
    @keyframes float {
        0%, 100% {
            transform: translateY(0px);
        }
        50% {
            transform: translateY(-10px);
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.7;
        }
    }
`;

// Hook to detect when element is in viewport
const useInView = (threshold = 0.1) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        if (Platform.OS !== 'web') {
            setIsInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                }
            },
            { threshold }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold]);

    return { ref, isInView };
};

/**
 * Restaurants Screen - Cape Roots Restaurant Wholesale Page
 * Information page for restaurant clients about wholesale services
 */
export const CapeRootsRestaurants = () => {
    const theme = useTheme();
    const isFocused = useIsFocused();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { updateSplashLoaded, updateShowNavigationBar, screenHeroConfigs } = navigationStore((state) => state);
    const { config } = useStoreConfig();
    const { navigateToScreen } = useScreenComponent();
    const scrollViewRef = useRef<ScrollView>(null);
    const [signupFormOpen, setSignupFormOpen] = useState(false);

    // Get hero config for this screen (video URL comes from database via API)
    const heroConfig: ScreenHeroConfig = screenHeroConfigs['CapeRootsRestaurants'] || {};

    useEffect(() => {
        updateSplashLoaded(true);
    }, []);

    useEffect(() => {
        if (isFocused) {
            updateShowNavigationBar(true);
        }
    }, [isFocused]);

    // Inject keyframes into document
    useEffect(() => {
        if (Platform.OS === 'web') {
            const styleSheet = document.createElement('style');
            styleSheet.innerText = keyframes;
            document.head.appendChild(styleSheet);
            return () => {
                document.head.removeChild(styleSheet);
            };
        }
    }, []);

    const scrollToTop = () => {
        if (Platform.OS === 'web') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            scrollViewRef.current?.scrollTo({ y: 0, animated: true });
        }
    };

    return (
        <ScrollView
            ref={scrollViewRef}
            style={{
                flex: 1,
                backgroundColor: theme.palette.background.default,
            }}
            contentContainerStyle={styles.scrollContent}
        >
            {/* Hero Section */}
            <HeroSection 
                config={config} 
                theme={theme} 
                isMobile={isMobile}
                isTablet={isTablet}
                heroConfig={heroConfig}
            />

            {/* Commitment Section */}
            <CommitmentSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* Signup Process Section */}
            <SignupProcessSection 
                theme={theme} 
                config={config} 
                isMobile={isMobile} 
                isTablet={isTablet}
                onOpenSignupForm={() => setSignupFormOpen(true)}
            />

            {/* Why Choose Us Section */}
            <WhyChooseUsSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* Order Information Section */}
            <OrderInfoSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* Contact Section */}
            <ContactSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* CTA Section */}
            <CTASection 
                theme={theme} 
                config={config} 
                isMobile={isMobile}
                onBrowseProducts={() => navigateToScreen(ScreenComponent.Shop)}
            />

            {/* Restaurant Signup Form Modal */}
            <RestaurantSignupForm
                open={signupFormOpen}
                onClose={() => setSignupFormOpen(false)}
            />

            {/* Scroll to Top FAB */}
            <Fab
                size={isMobile ? 'medium' : 'large'}
                onClick={scrollToTop}
                sx={{
                    position: 'fixed',
                    bottom: isMobile ? 80 : 32,
                    right: isMobile ? 16 : 32,
                    backgroundColor: config.primaryColor,
                    color: 'white',
                    boxShadow: `0 8px 32px ${config.primaryColor}50, 0 4px 12px rgba(0,0,0,0.15)`,
                    animation: 'float 2s ease-in-out infinite',
                    '&:hover': {
                        backgroundColor: config.primaryColor,
                        transform: 'scale(1.1)',
                        boxShadow: `0 12px 40px ${config.primaryColor}70, 0 6px 16px rgba(0,0,0,0.2)`,
                    },
                    transition: 'all 0.3s ease',
                    zIndex: 1000,
                }}
            >
                <KeyboardArrowUpIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
            </Fab>
        </ScrollView>
    );
};

// Hero Section Component
interface RestaurantHeroSectionProps {
    config: any;
    theme: any;
    isMobile: boolean;
    isTablet: boolean;
    heroConfig?: ScreenHeroConfig;
}

const HeroSection = ({ config, theme, isMobile, isTablet, heroConfig }: RestaurantHeroSectionProps) => {
    const { ref, isInView } = useInView(0.1);
    const hasVideoBackground = !!heroConfig?.heroVideoUrl || !!heroConfig?.heroImageUrl;

    // Content for the hero section
    const heroContent = (
        <>
            <Box
                sx={{
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'scaleIn 0.8s ease-out forwards' : 'none',
                    marginBottom: 3,
                }}
            >
                <Box sx={{
                    width: isMobile ? 80 : 100,
                    height: isMobile ? 80 : 100,
                    borderRadius: '50%',
                    backgroundColor: hasVideoBackground ? 'rgba(255,255,255,0.2)' : `${config.primaryColor}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    animation: 'float 3s ease-in-out infinite',
                    backdropFilter: hasVideoBackground ? 'blur(10px)' : 'none',
                }}>
                    <RestaurantIcon sx={{
                        fontSize: isMobile ? 40 : 50,
                        color: hasVideoBackground ? '#FFFFFF' : config.primaryColor
                    }} />
                </Box>
            </Box>

            <Typography 
                variant={isMobile ? 'h4' : 'h3'}
                sx={{ 
                    fontWeight: 700,
                    marginBottom: 2,
                    color: hasVideoBackground ? '#FFFFFF' : theme.palette.text.primary,
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                    animationFillMode: 'backwards',
                    maxWidth: 800,
                    lineHeight: 1.2,
                    textShadow: hasVideoBackground ? '0 2px 20px rgba(0,0,0,0.6)' : 'none',
                }}
            >
                Restaurants
            </Typography>

            <Typography 
                variant={isMobile ? 'h6' : 'h5'}
                sx={{ 
                    fontWeight: 300,
                    color: hasVideoBackground ? 'rgba(255,255,255,0.95)' : config.primaryColor,
                    marginBottom: 3,
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.4s forwards' : 'none',
                    animationFillMode: 'backwards',
                    textShadow: hasVideoBackground ? '0 2px 10px rgba(0,0,0,0.5)' : 'none',
                }}
            >
                Fruit & Vegetable Wholesale Supplier for Restaurants
            </Typography>
            
            <Typography 
                variant={isMobile ? 'body1' : 'h6'}
                sx={{ 
                    color: hasVideoBackground ? 'rgba(255,255,255,0.9)' : theme.palette.text.secondary,
                    marginBottom: 4,
                    maxWidth: 700,
                    fontWeight: 400,
                    lineHeight: 1.8,
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.6s forwards' : 'none',
                    animationFillMode: 'backwards',
                    px: 2,
                    textShadow: hasVideoBackground ? '0 1px 8px rgba(0,0,0,0.5)' : 'none',
                }}
            >
                At Cape Roots, we are dedicated to delivering exceptional quality and outstanding 
                service to all our restaurant clients across Cape Town.
            </Typography>
        </>
    );

    // Render with video/image background if configured
    if (hasVideoBackground) {
        return (
            <Box ref={ref}>
                <VideoBackground
                    videoUrl={heroConfig?.heroVideoUrl}
                    imageUrl={heroConfig?.heroImageUrl}
                    overlayOpacity={heroConfig?.heroOverlayOpacity ?? 0.4}
                    minHeight={isMobile ? '70vh' : '60vh'}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: isMobile ? 3 : 6,
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                        }}
                    >
                        {heroContent}
                    </Box>
                </VideoBackground>
            </Box>
        );
    }

    // Render default gradient background
    return (
        <Box
            ref={ref}
            sx={{
                minHeight: isMobile ? '70vh' : '60vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? 3 : 6,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${config.primaryColor}08 50%, ${theme.palette.background.default} 100%)`,
            }}
        >
            {/* Decorative background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '15%',
                    right: '10%',
                    width: isMobile ? 120 : 200,
                    height: isMobile ? 120 : 200,
                    borderRadius: '50%',
                    background: `${config.primaryColor}10`,
                    filter: 'blur(60px)',
                    animation: 'pulse 4s ease-in-out infinite',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '5%',
                    width: isMobile ? 100 : 180,
                    height: isMobile ? 100 : 180,
                    borderRadius: '50%',
                    background: `${config.primaryColor}08`,
                    filter: 'blur(80px)',
                    animation: 'pulse 5s ease-in-out infinite',
                }}
            />

            {heroContent}
        </Box>
    );
};

// Commitment Section
const CommitmentSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 10,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box sx={{
                maxWidth: 1000,
                margin: '0 auto',
                textAlign: 'center',
            }}>
                <Box
                    sx={{
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Our Commitment
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h5' : 'h4'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            marginBottom: 3,
                        }}
                    >
                        Excellence in Every Delivery
                    </Typography>
                </Box>

                <Typography 
                    variant='body1'
                    sx={{ 
                        color: theme.palette.text.secondary,
                        marginBottom: 3,
                        lineHeight: 1.9,
                        maxWidth: 800,
                        margin: '0 auto 24px',
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    We are committed to excellence, striving to exceed our customers' expectations 
                    in every interaction. Through personalized support and tailored solutions, we 
                    aim to create an experience that delights our clients.
                </Typography>

                <Typography 
                    variant='body1'
                    sx={{ 
                        color: theme.palette.text.secondary,
                        lineHeight: 1.9,
                        maxWidth: 800,
                        margin: '0 auto',
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.4s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    Your satisfaction is our top priority, and we take pride in building lasting 
                    relationships through our unwavering dedication to service excellence.
                </Typography>
            </Box>
        </Box>
    );
};

// Signup Process Section
const SignupProcessSection = ({ theme, config, isMobile, isTablet, onOpenSignupForm }: any) => {
    const { ref, isInView } = useInView(0.1);

    const steps = [
        {
            number: '1',
            icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
            title: 'Apply Online',
            description: 'Click the button below to fill out our quick online application form.',
        },
        {
            number: '2',
            icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
            title: 'We Review',
            description: 'Our team reviews your application and prepares your account.',
        },
        {
            number: '3',
            icon: <HandshakeIcon sx={{ fontSize: 28 }} />,
            title: 'Get Started',
            description: 'We contact you to finalize details and you can start ordering!',
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 12,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        marginBottom: isMobile ? 5 : 8,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Restaurant Signup
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            marginBottom: 2,
                        }}
                    >
                        How to Become a Client
                    </Typography>
                    <Typography 
                        variant='body1'
                        sx={{ 
                            color: theme.palette.text.secondary,
                            maxWidth: 600,
                            margin: '0 auto',
                        }}
                    >
                        Join Cape Roots as a wholesale client in just 3 simple steps
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isTablet 
                        ? isMobile ? '1fr' : 'repeat(2, 1fr)' 
                        : 'repeat(3, 1fr)',
                    gap: 3,
                    maxWidth: 900,
                    margin: '0 auto',
                }}>
                    {steps.map((step, index) => (
                        <Box
                            key={step.title}
                            sx={{
                                padding: isMobile ? 3 : 4,
                                borderRadius: 4,
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                textAlign: 'center',
                                position: 'relative',
                                opacity: isInView ? 1 : 0,
                                animation: isInView ? `fadeInUp 0.6s ease-out ${0.1 + index * 0.15}s forwards` : 'none',
                                animationFillMode: 'backwards',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: config.primaryColor,
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 10px 30px ${config.primaryColor}15`,
                                },
                            }}
                        >
                            {/* Step Number Badge */}
                            <Box sx={{
                                position: 'absolute',
                                top: -15,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                backgroundColor: config.primaryColor,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: 14,
                            }}>
                                {step.number}
                            </Box>

                            <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: `${config.primaryColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '16px auto 20px',
                                color: config.primaryColor,
                            }}>
                                {step.icon}
                            </Box>
                            <Typography 
                                variant='h6'
                                sx={{ 
                                    fontWeight: 600,
                                    marginBottom: 1.5,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {step.title}
                            </Typography>
                            <Typography 
                                variant='body2'
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.7,
                                }}
                            >
                                {step.description}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Signup Button */}
                <Box sx={{ 
                    textAlign: 'center', 
                    marginTop: 5,
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.8s forwards' : 'none',
                    animationFillMode: 'backwards',
                }}>
                    <Button
                        variant='contained'
                        size='large'
                        onClick={onOpenSignupForm}
                        startIcon={<AssignmentIcon />}
                        sx={{
                            backgroundColor: config.primaryColor,
                            '&:hover': {
                                backgroundColor: config.primaryColor,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 25px ${config.primaryColor}40`,
                            },
                            paddingX: isMobile ? 4 : 6,
                            paddingY: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Apply Now
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Why Choose Us Section
const WhyChooseUsSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    const features = [
        {
            icon: <LocalShippingIcon sx={{ fontSize: 32 }} />,
            title: 'Reliable Delivery',
            description: 'Fresh produce delivered to your restaurant on time, every time.',
        },
        {
            icon: <HandshakeIcon sx={{ fontSize: 32 }} />,
            title: 'Tailored Solutions',
            description: 'Personalized support and solutions designed for your specific needs.',
        },
        {
            icon: <SupportAgentIcon sx={{ fontSize: 32 }} />,
            title: 'Dedicated Support',
            description: 'Direct contact with our team for any questions or special requests.',
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 10,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        marginBottom: isMobile ? 5 : 8,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Why Cape Roots
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Your Partner in Quality
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    gap: 4,
                    justifyContent: 'center',
                    alignItems: isTablet ? 'center' : 'stretch',
                }}>
                    {features.map((feature, index) => (
                        <Box
                            key={feature.title}
                            sx={{
                                flex: 1,
                                maxWidth: isTablet ? 400 : 350,
                                width: '100%',
                                textAlign: 'center',
                                padding: isMobile ? 3 : 4,
                                opacity: isInView ? 1 : 0,
                                animation: isInView ? `scaleIn 0.6s ease-out ${0.2 + index * 0.15}s forwards` : 'none',
                                animationFillMode: 'backwards',
                            }}
                        >
                            <Box sx={{
                                width: 70,
                                height: 70,
                                borderRadius: '50%',
                                backgroundColor: `${config.primaryColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                color: config.primaryColor,
                            }}>
                                {feature.icon}
                            </Box>
                            <Typography 
                                variant='h6'
                                sx={{ 
                                    fontWeight: 600,
                                    marginBottom: 1.5,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {feature.title}
                            </Typography>
                            <Typography 
                                variant='body2'
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.7,
                                }}
                            >
                                {feature.description}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// Order Information Section
const OrderInfoSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 10,
                px: isMobile ? 2 : 4,
                backgroundColor: config.primaryColor,
            }}
        >
            <Box sx={{
                maxWidth: 900,
                margin: '0 auto',
                textAlign: 'center',
            }}>
                <Box sx={{
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                }}>
                    <AccessTimeIcon sx={{ fontSize: 50, color: 'white', marginBottom: 2 }} />
                    <Typography 
                        variant={isMobile ? 'h5' : 'h4'}
                        sx={{ 
                            fontWeight: 700,
                            color: 'white',
                            marginBottom: 3,
                        }}
                    >
                        How to Place Orders
                    </Typography>
                </Box>

                <Typography 
                    variant='body1'
                    sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        marginBottom: 4,
                        lineHeight: 1.9,
                        maxWidth: 700,
                        margin: '0 auto 32px',
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    Orders may be placed through <strong>phone</strong>, <strong>email</strong>, or <strong>WhatsApp</strong>. 
                    It is essential that all orders be submitted no later than <strong>11 PM</strong> on the day 
                    before delivery, which requires placement the preceding night.
                </Typography>

                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2,
                    justifyContent: 'center',
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.4s forwards' : 'none',
                    animationFillMode: 'backwards',
                }}>
                    <Button
                        variant='contained'
                        startIcon={<PhoneIcon />}
                        onClick={() => Linking.openURL('tel:0785866662')}
                        sx={{
                            backgroundColor: 'white',
                            color: config.primaryColor,
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.9)',
                            },
                            paddingX: 3,
                            paddingY: 1.5,
                            fontWeight: 600,
                            borderRadius: 3,
                        }}
                    >
                        Call Us
                    </Button>
                    <Button
                        variant='contained'
                        startIcon={<WhatsAppIcon />}
                        onClick={() => Linking.openURL('https://wa.me/27785866662')}
                        sx={{
                            backgroundColor: 'white',
                            color: config.primaryColor,
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.9)',
                            },
                            paddingX: 3,
                            paddingY: 1.5,
                            fontWeight: 600,
                            borderRadius: 3,
                        }}
                    >
                        WhatsApp
                    </Button>
                    <Button
                        variant='contained'
                        startIcon={<EmailIcon />}
                        onClick={() => Linking.openURL('mailto:orders@caperoots.co.za')}
                        sx={{
                            backgroundColor: 'white',
                            color: config.primaryColor,
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.9)',
                            },
                            paddingX: 3,
                            paddingY: 1.5,
                            fontWeight: 600,
                            borderRadius: 3,
                        }}
                    >
                        Email Order
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// Contact Section
const ContactSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 10,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Box sx={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
                <Box
                    sx={{
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Questions?
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h5' : 'h4'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            marginBottom: 2,
                        }}
                    >
                        Need Further Assistance?
                    </Typography>
                    <Typography 
                        variant='body1'
                        sx={{ 
                            color: theme.palette.text.secondary,
                            marginBottom: 4,
                        }}
                    >
                        If you have any questions or need further assistance with our product range, 
                        please do not hesitate to contact us.
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 3,
                    justifyContent: 'center',
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                    animationFillMode: 'backwards',
                }}>
                    <Box sx={{
                        padding: 3,
                        borderRadius: 3,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        minWidth: isMobile ? 'auto' : 200,
                    }}>
                        <PhoneIcon sx={{ color: config.primaryColor, fontSize: 28, marginBottom: 1 }} />
                        <Typography variant='subtitle2' sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                            Nicholas
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            078 586 6662
                        </Typography>
                    </Box>
                    <Box sx={{
                        padding: 3,
                        borderRadius: 3,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        minWidth: isMobile ? 'auto' : 200,
                    }}>
                        <EmailIcon sx={{ color: config.primaryColor, fontSize: 28, marginBottom: 1 }} />
                        <Typography variant='subtitle2' sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                            Email
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            info@caperoots.co.za
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// CTA Section
const CTASection = ({ theme, config, isMobile, onBrowseProducts }: any) => {
    const { ref, isInView } = useInView(0.2);

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 10,
                px: isMobile ? 3 : 4,
                backgroundColor: theme.palette.background.paper,
                textAlign: 'center',
            }}
        >
            <Box sx={{ maxWidth: 700, margin: '0 auto' }}>
                <Typography 
                    variant={isMobile ? 'h5' : 'h4'}
                    sx={{ 
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        marginBottom: 2,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    Ready to Browse Our Products?
                </Typography>
                <Typography 
                    variant='body1'
                    sx={{ 
                        color: theme.palette.text.secondary,
                        marginBottom: 4,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    Explore our wide selection of fresh fruits and vegetables available for wholesale.
                </Typography>
                <Button
                    variant='contained'
                    size='large'
                    onClick={onBrowseProducts}
                    sx={{
                        backgroundColor: config.primaryColor,
                        '&:hover': {
                            backgroundColor: config.primaryColor,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 25px ${config.primaryColor}40`,
                        },
                        paddingX: isMobile ? 5 : 6,
                        paddingY: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.4s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    View Products
                </Button>
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    scrollContent: {
        flexGrow: 1,
    },
});

export default CapeRootsRestaurants;
